import React, { useMemo } from 'react';
import { useStyletron } from 'baseui';
import { Modal, ROLE as MODAL_ROLE, SIZE as MODAL_SIZE } from 'baseui/modal';
import noop from 'lodash/noop';
import { useDeleteHandler as useDeleteAnnotationHandler } from '../../side-panel/annotation-details/hooks/use-delete-handler';
import { useDeleteHandler as useDeleteAllAnnotationsHandler } from '../../side-panel/annotation-header/hooks/use-delete-handler';
import { useAnnotationItemContainerActions } from '../../side-panel/annotations-common/hooks/use-annotation-item-container-actions';
import { getLabelMappedAnnotations } from '../../side-panel/annotations-common/utils';
import { getLabelIcon } from '../../side-panel/annotations-common/utils';
import { DeleteAllAnnotationsConfirmationModal } from '../../side-panel/common/delete-all-annotations-confirmation-modal';
import { EmptyState } from '../../side-panel/common/empty-state';
import { useFilteredAnnotations } from '../../side-panel/hooks/use-filtered-annotations';
import { useFocusedAnnotationId } from '../../side-panel/hooks/use-focused-annotation';
import { useSelectedAnnotationIds } from '../../side-panel/hooks/use-selected-annotation-ids';
import { useSidePanel } from '../../side-panel/side-panel-provider';
import { useAnnotationSectionExpansion } from './hooks/use-annotation-section-expansion';
import { AnnotationAccordion } from './annotation-accordion';
import { AnnotationSectionContent } from './annotation-section-content';
import { AnnotationDetailsHeader } from './annotations-detail-header';
const ICON_SIZE = 14;
const EMPTY_STATE_SIZE = 50;
export const AnnotationList = () => {
  const annotations = useFilteredAnnotations();
  const {
    toolConfig,
    deleteAnnotationModalContentComponent
  } = useSidePanel();
  const {
    handleDeleteAnnotation,
    deleteModalProps: deleteAnnotationModalProps
  } = useDeleteAnnotationHandler();
  const {
    handleDeleteAllAnnotations,
    deleteModalProps: deleteAllAnnotationsModalProps
  } = useDeleteAllAnnotationsHandler();
  const selectedAnnotationIds = useSelectedAnnotationIds();
  const focusedAnnotationId = useFocusedAnnotationId();
  const {
    onMouseEnter,
    onMouseLeave,
    onKeyDown
  } = useAnnotationItemContainerActions({
    annotations
  });
  const hasAnnotations = annotations.length > 0;
  const shouldShowDeleteAnnotationModal = !!deleteAnnotationModalContentComponent;
  const deleteAllAnnotationsProps = deleteAllAnnotationsModalProps.isOpen ? deleteAllAnnotationsModalProps : {
    isOpen: false,
    label: '',
    onCancel: noop,
    onConfirm: () => Promise.resolve()
  };
  const labelMappedAnnotations = useMemo(() => getLabelMappedAnnotations(annotations, []), [annotations]);
  const [, theme] = useStyletron();
  const annotationSections = useMemo(() => {
    return Object.entries(labelMappedAnnotations).map(([label, labelDetails]) => {
      const {
        headerInfo,
        annotations: labelAnnotations
      } = labelDetails;
      const icon = getLabelIcon(headerInfo.iconConfig, toolConfig, ICON_SIZE);
      return {
        id: label,
        header: <AnnotationDetailsHeader headerInfo={headerInfo} label={label} icon={icon} onDeleteAllAnnotations={() => handleDeleteAllAnnotations(label)} />,
        items: [<AnnotationSectionContent key={`${label}-items`} annotations={labelAnnotations} iconConfig={headerInfo.iconConfig} iconSize={ICON_SIZE} selectedAnnotationIds={selectedAnnotationIds} focusedAnnotationId={focusedAnnotationId} labelColor={headerInfo.iconConfig?.color} onDeleteAnnotation={handleDeleteAnnotation} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onKeyDown={onKeyDown} />]
      };
    });
  }, [focusedAnnotationId, handleDeleteAllAnnotations, handleDeleteAnnotation, onKeyDown, onMouseEnter, onMouseLeave, selectedAnnotationIds, toolConfig, labelMappedAnnotations]);
  const allSectionKeys = useMemo(() => Object.keys(labelMappedAnnotations), [labelMappedAnnotations]);
  const {
    expandedKeys,
    setExpandedKeys
  } = useAnnotationSectionExpansion({
    allSectionKeys
  });
  if (!hasAnnotations) {
    return <EmptyState size={EMPTY_STATE_SIZE} message="No annotations found. Add annotations or adjust your filters." />;
  }
  return <div>
      <AnnotationAccordion sections={annotationSections} expandedKeys={expandedKeys} onChange={keys => {
      setExpandedKeys(keys);
    }} headerBackgroundColor={theme.colors.backgroundSecondary} />

      {shouldShowDeleteAnnotationModal && <Modal onClose={deleteAnnotationModalProps.isOpen ? deleteAnnotationModalProps.onCancel : undefined} closeable isOpen={deleteAnnotationModalProps.isOpen} animate autoFocus size={MODAL_SIZE.default} role={MODAL_ROLE.dialog}>
          {deleteAnnotationModalProps.isOpen && deleteAnnotationModalContentComponent({
        onCancel: deleteAnnotationModalProps.onCancel,
        onSubmit: deleteAnnotationModalProps.onSubmit,
        annotationId: deleteAnnotationModalProps.annotationId
      })}
        </Modal>}

      <DeleteAllAnnotationsConfirmationModal {...deleteAllAnnotationsProps} />
    </div>;
};